import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { userSelectors } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type TraineeStatus } from "@fitness-app/data-models/entities/Trainee";
import { fetchAllPostgrestRows } from "@fitness-app/utils/src/helpers/fetchAllPostgrestRows";

import { usePermissionScope } from "~/hooks/usePermission";
import { supabase, useAppSelector } from "~/store/initializeStore";

import { resolveTargetedTrainees } from "./resolveTargetedTrainees";
import { type TargetedTrainee, type TraineeTargetSpec } from "./types";

const PRODUCT_ACTIVE_STATUS = "active";

type TraineeRow = {
  id: string;
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  tags: unknown;
  status: string;
};

/** `null` = no narrowing (whole tenant), `undefined` = scope not resolved yet, `[]` = nobody. */
type TrainerIdScope = string[] | null | undefined;

/**
 * Loads the org's trainees (with tags) and their active product memberships, then resolves a
 * {@link TraineeTargetSpec} to the concrete trainee list. RLS scopes the queries to the caller's org.
 */
export const useTraineeTargeting = (spec: TraineeTargetSpec, enabled = true) => {
  // Scope the audience by the caller's `clients.read` scope, NOT by the profile role: a head coach
  // is a TEAM_MEMBER with clients.read ALL, and narrowing by `assignedTrainerId` cut the audience
  // down to the dozen clients assigned to them personally (TRE-2037). RLS scopes to the tenant and
  // already honours the same scope; this only mirrors it so the preview counts match what will save.
  const { memberId, isAccountOwner } = useAppSelector(userSelectors.getUserClaims);
  const clientsScope = usePermissionScope(PermissionKey.CLIENTS_READ);
  // The owner's claims carry ALL, but a legacy session (profile role TRAINER, permissions not yet
  // refreshed) would resolve to NONE and blank the panel — keep the explicit escape hatch.
  const scope = isAccountOwner ? PermissionScope.ALL : clientsScope;

  const headCoachId = scope === PermissionScope.TEAM ? (memberId ?? null) : null;

  const subordinatesQuery = useQuery({
    queryKey: ["trainee-targeting", "head-coach-team", headCoachId],
    enabled: enabled && !!headCoachId,
    staleTime: 60_000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("team_member_assigned")
        .select("assignedTrainerId")
        .eq("headCoachId", headCoachId!)
        .returns<{ assignedTrainerId: string }[]>();

      if (error) {
        throw error;
      }

      return [headCoachId!, ...data.map((row) => row.assignedTrainerId)];
    },
  });

  const trainerIds = useMemo<TrainerIdScope>(() => {
    if (scope === PermissionScope.ALL) {
      return null;
    }
    if (scope === PermissionScope.NONE || !memberId) {
      return [];
    }
    if (scope === PermissionScope.ASSIGNED) {
      return [memberId];
    }
    // TEAM — own clients plus those of the subordinates; undefined until that list lands, so the
    // trainee query does not fire unnarrowed in the meantime. A failed lookup falls back to the
    // head coach's own clients instead of hanging the panel on a spinner forever.
    if (subordinatesQuery.isError) {
      return [memberId];
    }
    return subordinatesQuery.data;
  }, [scope, memberId, subordinatesQuery.data, subordinatesQuery.isError]);

  const traineesQuery = useQuery({
    queryKey: ["trainee-targeting", "trainees", trainerIds ?? "all"],
    enabled: enabled && trainerIds !== undefined,
    staleTime: 60_000,
    queryFn: async (): Promise<TargetedTrainee[]> => {
      if (trainerIds && trainerIds.length === 0) {
        return [];
      }

      // Paged by `id`, not `firstName`: paging needs a unique key, so the alphabetical order the
      // panel shows is applied below instead of in the query.
      const rows = await fetchAllPostgrestRows<TraineeRow>(({ from, to, withCount }) => {
        let query = supabase
          .from("trainee")
          .select("id, userId, firstName, lastName, email, tags, status", withCount ? { count: "exact" } : undefined)
          .order("id", { ascending: true })
          .range(from, to);

        if (trainerIds) {
          query = query.in("assignedTrainerId", trainerIds);
        }

        return query.returns<TraineeRow[]>();
      });

      return rows
        .map((row) => ({
          id: row.id,
          userId: row.userId,
          firstName: row.firstName ?? "",
          lastName: row.lastName,
          email: row.email,
          tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
          status: row.status as TraineeStatus,
        }))
        .sort((a, b) => a.firstName.localeCompare(b.firstName, "pl"));
    },
  });

  const membershipQuery = useQuery({
    queryKey: ["trainee-targeting", "product-membership"],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<Record<string, string[]>> => {
      const rows = await fetchAllPostgrestRows<{ traineeId: string | null; productId: string | null }>(
        ({ from, to, withCount }) =>
          supabase
            .from("product_client")
            .select("traineeId, productId", withCount ? { count: "exact" } : undefined)
            .eq("status", PRODUCT_ACTIVE_STATUS)
            .order("id", { ascending: true })
            .range(from, to)
            .returns<{ traineeId: string | null; productId: string | null }[]>(),
      );

      const map: Record<string, string[]> = {};
      for (const row of rows) {
        if (!row.traineeId || !row.productId) {
          continue;
        }
        (map[row.traineeId] ??= []).push(row.productId);
      }
      return map;
    },
  });

  const trainees = useMemo(() => traineesQuery.data ?? [], [traineesQuery.data]);
  const membership = useMemo(() => membershipQuery.data ?? {}, [membershipQuery.data]);

  const resolved = useMemo(() => resolveTargetedTrainees(trainees, membership, spec), [trainees, membership, spec]);

  return {
    trainees,
    resolved,
    resolvedCount: resolved.length,
    // `isLoading` stays true on a disabled react-query v4 query, so the still-resolving scope is
    // expressed by `trainerIds === undefined` rather than by the subordinates query's own flag.
    isLoading: (enabled && trainerIds === undefined) || traineesQuery.isLoading || membershipQuery.isLoading,
    isError: traineesQuery.isError || membershipQuery.isError,
  };
};
